/**
 * Unified PromptInput Component
 *
 * Flexible prompt input that supports:
 * - Single prompt mode
 * - Prompt + negative prompt mode (image generation)
 * - Title + description mode (AI generation)
 * - Optional generate button
 *
 * Consolidates: image/PromptInput, thumbnail-studio/ai/AIPromptInput
 */

import React, { useState, useCallback, memo, forwardRef } from 'react';
import { ChevronDown, ChevronUp, Sparkles } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/lib/utils';

// ============================================================================
// Types
// ============================================================================

export type PromptMode = 'single' | 'with-negative' | 'title-description';

interface BasePromptInputProps {
  /** Primary prompt/title value */
  value: string;
  /** Callback when primary value changes */
  onChange: (value: string, secondary?: string) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Label text */
  label?: string;
  /** Whether component is disabled */
  disabled?: boolean;
  /** Additional class name */
  className?: string;
  /** Minimum rows for textarea */
  minRows?: number;
  /** Maximum rows for textarea */
  maxRows?: number;
  /** Maximum character length */
  maxLength?: number;
  /** Show character count */
  showCharCount?: boolean;
  /** Test ID for testing */
  testId?: string;
}

// Single prompt mode
export interface SingleModeProps extends BasePromptInputProps {
  mode: 'single';
}

// Prompt + negative prompt mode (image generation)
export interface WithNegativeProps extends BasePromptInputProps {
  mode: 'with-negative';
  /** Negative prompt value */
  negativePrompt: string;
  /** Callback when negative prompt changes */
  onNegativePromptChange: (value: string) => void;
  /** Negative prompt placeholder */
  negativePlaceholder?: string;
  /** Negative prompt label */
  negativeLabel?: string;
}

// Title + description mode (AI generation)
export interface TitleDescriptionProps extends BasePromptInputProps {
  mode: 'title-description';
  /** Description value */
  description?: string;
  /** Callback when generate is clicked */
  onGenerate: () => void;
  /** Whether generation is in progress */
  isGenerating?: boolean;
  /** Generate button text */
  generateButtonText?: string;
  /** Description placeholder */
  descriptionPlaceholder?: string;
}

export type PromptInputProps = SingleModeProps | WithNegativeProps | TitleDescriptionProps;

// ============================================================================
// Component
// ============================================================================

export const PromptInput = memo(
  forwardRef<HTMLTextAreaElement | HTMLInputElement, PromptInputProps>(
    function PromptInput(props, ref) {
      const { t } = useTranslation();
      const {
        value,
        onChange,
        placeholder,
        label,
        disabled = false,
        className = '',
        minRows = 3,
        maxRows = 10,
        maxLength,
        showCharCount = false,
        testId,
      } = props;

      const [showSecondary, setShowSecondary] = useState(
        props.mode === 'with-negative'
          ? !!props.negativePrompt
          : props.mode === 'title-description'
            ? !!props.description
            : false
      );

      // Handle primary value change
      const handlePrimaryChange = useCallback(
        (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
          const newValue = e.target.value;
          if (maxLength && newValue.length > maxLength) return;
          onChange(newValue);
        },
        [onChange, maxLength]
      );

      // Handle keyboard events for title-description mode
      const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
          if (props.mode === 'title-description') {
            if (e.key === 'Enter' && !e.shiftKey && value.trim() && !props.isGenerating) {
              e.preventDefault();
              props.onGenerate();
            }
          }
        },
        [props, value]
      );

      // Toggle secondary field
      const toggleSecondary = useCallback(() => {
        setShowSecondary((prev) => !prev);
      }, []);

      // Render single mode
      if (props.mode === 'single') {
        return (
          <div className={cn('space-y-2', className)}>
            {label && <label className="form-label">{label}</label>}
            <textarea
              ref={ref as React.Ref<HTMLTextAreaElement>}
              value={value}
              onChange={handlePrimaryChange}
              placeholder={placeholder || t('prompt.placeholder', 'Enter your prompt...')}
              className="input min-h-[120px] resize-y"
              disabled={disabled}
              rows={minRows}
              style={{
                minHeight: `${minRows * 1.5}rem`,
                maxHeight: maxRows ? `${maxRows * 1.5}rem` : undefined,
              }}
              data-testid={testId}
            />
            {showCharCount && maxLength && (
              <div className="text-xs text-secondary-500 text-right">
                {value.length}/{maxLength}
              </div>
            )}
          </div>
        );
      }

      // Render with-negative mode (image generation)
      if (props.mode === 'with-negative') {
        const negativeLabel =
          props.negativeLabel || t('image.prompt.negative.label', 'Negative Prompt');
        const negativePlaceholder =
          props.negativePlaceholder ||
          t('image.prompt.negative.placeholder', 'What to avoid in the image...');

        return (
          <div className={cn('space-y-4', className)}>
            {/* Main Prompt */}
            <div className="form-group">
              <label htmlFor="prompt" className="form-label">
                {label || t('image.prompt.label', 'Prompt')} *
              </label>
              <textarea
                id="prompt"
                ref={ref as React.Ref<HTMLTextAreaElement>}
                value={value}
                onChange={handlePrimaryChange}
                placeholder={
                  placeholder ||
                  t('image.prompt.placeholder', 'Describe the image you want to create...')
                }
                className="input min-h-[120px] resize-y"
                disabled={disabled}
                data-testid={testId || 'image-prompt-input'}
              />
            </div>

            {/* Negative Prompt Toggle */}
            <button
              type="button"
              onClick={toggleSecondary}
              className="flex items-center gap-2 text-sm text-secondary-600 hover:text-secondary-800 dark:text-secondary-400 dark:hover:text-secondary-200 transition-colors"
              disabled={disabled}
            >
              {showSecondary ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
              <span>
                {showSecondary
                  ? t('image.prompt.negative.hide', 'Hide negative prompt')
                  : t('image.prompt.negative.toggle', 'Add negative prompt')}
              </span>
            </button>

            {/* Negative Prompt */}
            {showSecondary && (
              <div className="form-group animate-in slide-in-from-top-2 duration-200">
                <label htmlFor="negative-prompt" className="form-label">
                  {negativeLabel}
                </label>
                <textarea
                  id="negative-prompt"
                  value={props.negativePrompt}
                  onChange={(e) => props.onNegativePromptChange(e.target.value)}
                  placeholder={negativePlaceholder}
                  className="input min-h-[80px] resize-y"
                  disabled={disabled}
                  data-testid="image-negative-prompt-input"
                />
                <p className="text-xs text-secondary-500 mt-1">
                  {t(
                    'image.prompt.negative.hint',
                    'Describe elements you want to exclude from the generated image'
                  )}
                </p>
              </div>
            )}
          </div>
        );
      }

      // Render title-description mode (AI thumbnail generation)
      if (props.mode === 'title-description') {
        const descriptionPlaceholder =
          props.descriptionPlaceholder ||
          t(
            'thumbnailStudio.ai.descriptionPlaceholder',
            'Add more context about your video content...'
          );
        const generateText =
          props.generateButtonText ||
          (props.isGenerating
            ? t('thumbnailStudio.ai.generating', 'Generating...')
            : t('thumbnailStudio.ai.generate', 'Generate'));

        return (
          <div className={cn('space-y-3', className)}>
            {/* Main title input */}
            <div className="relative">
              <input
                type="text"
                ref={ref as React.Ref<HTMLInputElement>}
                value={value}
                onChange={handlePrimaryChange}
                onKeyDown={handleKeyDown}
                placeholder={
                  placeholder || t('thumbnailStudio.ai.enterTitle', 'Enter your video title...')
                }
                disabled={props.isGenerating}
                className="w-full px-4 py-3 pr-32 bg-gray-800 border border-gray-700 rounded-lg
                         text-white placeholder-gray-500 focus:outline-none focus:ring-2
                         focus:ring-purple-500 focus:border-transparent disabled:opacity-50
                         disabled:cursor-not-allowed"
                data-testid={testId}
              />
              <button
                onClick={props.onGenerate}
                disabled={!value.trim() || props.isGenerating}
                className="absolute right-2 top-1/2 -translate-y-1/2 px-4 py-1.5
                         bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500
                         hover:to-pink-500 text-white rounded-md font-medium text-sm
                         flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed
                         transition-all duration-200"
              >
                <Sparkles className="w-4 h-4" />
                {generateText}
              </button>
            </div>

            {/* Toggle description button */}
            <button
              onClick={toggleSecondary}
              className="flex items-center gap-1 text-sm text-gray-400 hover:text-gray-300 transition-colors"
            >
              {showSecondary ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
              {showSecondary
                ? t('thumbnailStudio.ai.hideDescription', 'Hide description')
                : t('thumbnailStudio.ai.addDescription', 'Add description (optional)')}
            </button>

            {/* Description textarea */}
            {showSecondary && (
              <textarea
                value={props.description || ''}
                onChange={(e) => onChange(value, e.target.value)}
                placeholder={descriptionPlaceholder}
                disabled={props.isGenerating}
                rows={3}
                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg
                         text-white placeholder-gray-500 focus:outline-none focus:ring-2
                         focus:ring-purple-500 focus:border-transparent disabled:opacity-50
                         disabled:cursor-not-allowed resize-none"
              />
            )}
          </div>
        );
      }

      return null;
    }
  )
);

export default PromptInput;
